// i18n.ts
import i18n from "i18next"
import { initReactI18next } from "react-i18next"
// Import all translations
import deTranslations from "@/locales/de.json"
import frTranslations from "@/locales/fr.json"
import esTranslations from "@/locales/es.json"
import itTranslations from "@/locales/it.json"
import svTranslations from "@/locales/sv.json"
import jaTranslations from "@/locales/ja.json"
import arTranslations from "@/locales/ar.json"
import ukTranslations from "@/locales/uk.json"
import { IS_TAZ } from "@/utils/brand"

// Define supported languages
const SUPPORTED_LANGUAGES = ["de", "fr", "es", "it", "sv", "ja", "ar", "uk"] as const
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]
const DEFAULT_LANGUAGE: SupportedLanguage | "en" = IS_TAZ ? "de" : "en"

// Type for the resources object
type Resources = {
    [key in SupportedLanguage | "en"]: {
        translation: Record<string, string>
    }
}

// Create resources object with all translations
const resources: Resources = {
    en: {
        translation: {
            fileProcessingFailureMissingSqlDerivative: "Missing IFC database",
            fileProcessingFailureUnsupportedIfcSchema: "Unsupported IFC schema",
            fileProcessingFailureIfcPropertyValidation: "IFC property validation failed",
            fileProcessingFailureInvalidDocument: "Invalid document",
            fileProcessingFailurePasswordProtected: "Password-protected document",
            fileProcessingFailureEmptyDocument: "Empty document",
        },
    },
    de: { translation: deTranslations },
    fr: { translation: frTranslations },
    es: { translation: esTranslations },
    it: { translation: itTranslations },
    sv: { translation: svTranslations },
    ja: { translation: jaTranslations },
    ar: { translation: arTranslations },
    uk: { translation: ukTranslations },
}

export const hasExplicitLanguagePreference = (): boolean => localStorage.getItem("googtrans") !== null

// Function to get saved language.
// English is persisted as googtrans=/auto/en (explicit preference) so FR browsers
// do not fall back to French after the user selects English.
export const getSavedLanguage = (): SupportedLanguage | "en" => {
    const savedLanguage = localStorage.getItem("googtrans")
    if (savedLanguage) {
        const cleanedLanguage = savedLanguage.replace("/auto/", "")
        if (cleanedLanguage === "en") {
            return "en"
        }
        return SUPPORTED_LANGUAGES.includes(cleanedLanguage as SupportedLanguage)
            ? (cleanedLanguage as SupportedLanguage)
            : DEFAULT_LANGUAGE
    }

    return DEFAULT_LANGUAGE
}

// Shared promise to prevent concurrent/duplicate initialization
let initPromise: Promise<void> | null = null

// Initialize i18n
export const initI18n = (): Promise<void> => {
    if (i18n.isInitialized) return Promise.resolve()
    if (initPromise) return initPromise

    const currentLanguage = getSavedLanguage()

    initPromise = i18n
        .use(initReactI18next)
        .init({
            resources,
            fallbackLng: "en",
            lng: currentLanguage,
            supportedLngs: ["en", ...SUPPORTED_LANGUAGES],
            keySeparator: false,
            interpolation: {
                escapeValue: false,
            },
        })
        .then(() => {})

    return initPromise
}

// Kick off initialization at module load time
if (!i18n.isInitialized) {
    initI18n().catch(console.error)
}

export const t = (key: string): string => i18n.t(key)

export default i18n
